import { useTheme } from '../../../context/ThemeContext';
import { useLang } from '../../../context/LanguageContext';

const socials = [
  {
    href: 'https://www.instagram.com/spartechrd?igsh=cXdsbGRhMTZrOXFw',
    icon: 'ri-instagram-line',
    label: 'Instagram',
    bgClass: 'bg-[#E1306C]',
    hoverClass: 'hover:bg-[#C13584]',
  },
  {
    href: 'https://www.facebook.com/profile.php?id=61552827808530',
    icon: 'ri-facebook-fill',
    label: 'Facebook',
    bgClass: 'bg-[#1877F2]',
    hoverClass: 'hover:bg-[#166fe5]',
  },
  {
    href: 'https://wa.me/18492081166',
    icon: 'ri-whatsapp-line',
    label: 'WhatsApp',
    bgClass: 'bg-[#25D366]',
    hoverClass: 'hover:bg-[#1ebe57]',
  },
  {
    href: 'https://www.tiktok.com/@spartechrd',
    icon: 'ri-tiktok-line',
    label: 'TikTok',
    bgClass: 'bg-[#000000]',
    hoverClass: 'hover:bg-[#000000]',
  },
];

const footerLinks = [
  { labelKey: 'nav_catalog', href: '/catalogo' },
  // { labelKey: 'nav_build', href: '/arma-tu-pc' },
  { labelKey: 'nav_services', href: '/servicios' },
  { labelKey: 'nav_about', href: '/nosotros' },
  { labelKey: 'nav_book', href: '/agendar' },
  { labelKey: 'tracking_title', href: '/seguimiento' },
];

export default function SiteFooter() {
  const { isDark } = useTheme();
  const { t } = useLang();
  const bg = isDark ? 'bg-zinc-950' : 'bg-zinc-900';
  const cardBg = isDark ? 'bg-zinc-900/50' : 'bg-zinc-800/40';
  const border = isDark ? 'border-zinc-800' : 'border-zinc-700';
  const textMuted = isDark ? 'text-zinc-400' : 'text-zinc-300';

  return (
    <footer className={`w-full ${bg} relative overflow-hidden`}>
      {/* Top accent glow */}
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-red-500 to-transparent opacity-60" />
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-64 h-24 bg-red-500/5 blur-3xl rounded-full pointer-events-none" />

      <div className="relative py-10 sm:py-16 px-4 sm:px-6">
        <div className="max-w-screen-xl mx-auto">
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-6 sm:gap-8 mb-10 sm:mb-12">
            {/* Brand */}
            <div className={`sm:col-span-2 md:col-span-1 ${cardBg} rounded-2xl p-5 sm:p-6 border ${border}`}>
              <img
                src="https://storage.readdy-site.link/project_files/e0ad0187-0f87-44ac-bc81-3163fcc0baf7/856a6a4e-c760-477d-a837-65755837c797_fb6affb0-c65b-4aac-81c4-bfd5377e0e55.webp?v=328770f95ae230a12f1326c5fd47e1ad"
                alt="SpartechRD"
                className="h-8 sm:h-10 w-auto object-contain mb-3 sm:mb-4"
                onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
              />
              <p className={`${textMuted} text-xs leading-relaxed mb-4 sm:mb-5`}>
                {t('footer_about')}
              </p>
              {/* Social icons */}
              <div className="flex items-center gap-2 sm:gap-2.5">
                {socials.map((s) => (
                  <a
                    key={s.label}
                    href={s.href}
                    target="_blank"
                    rel="nofollow noreferrer"
                    title={s.label}
                    className={`w-9 h-9 flex items-center justify-center rounded-xl ${s.bgClass} ${s.hoverClass} transition-all duration-200 cursor-pointer shadow-sm`}
                  >
                    <i className={`${s.icon} text-sm text-[#ffffff]`} />
                  </a>
                ))}
              </div>
            </div>

            {/* Quick links */}
            <div className={`${cardBg} rounded-2xl p-5 sm:p-6 border ${border}`}>
              <h4 className="text-white font-bold uppercase tracking-widest text-xs mb-4 sm:mb-5">{t('footer_nav')}</h4>
              <ul className="space-y-2 sm:space-y-2.5">
                {footerLinks.map((l) => (
                  <li key={l.labelKey}>
                    <a href={l.href} className={`${textMuted} hover:text-white text-sm transition-colors cursor-pointer flex items-center gap-1.5`}>
                      <i className="ri-arrow-right-s-line text-xs text-[#ffffff]" />
                      {t(l.labelKey)}
                    </a>
                  </li>
                ))}
              </ul>
            </div>

            {/* Hours + Contact */}
            <div className={`${cardBg} rounded-2xl p-5 sm:p-6 border ${border}`}>
              <h4 className="text-white font-bold uppercase tracking-widest text-xs mb-4 sm:mb-5">{t('footer_contact')}</h4>
              <div className="space-y-2.5 sm:space-y-3">
                <a href="tel:8295961615" className={`flex items-center gap-2 ${textMuted} hover:text-white transition-colors cursor-pointer`}>
                  <i className="ri-phone-line text-sm text-[#ffffff] flex-shrink-0" />
                  <span className="text-sm">(829) 596-1615</span>
                </a>
                <a href="tel:8492081166" className={`flex items-center gap-2 ${textMuted} hover:text-white transition-colors cursor-pointer`}>
                  <i className="ri-phone-line text-sm text-[#ffffff] flex-shrink-0" />
                  <span className="text-sm">(849) 208-1166</span>
                </a>
                <a href="mailto:ventas@spartechrd.com" className={`flex items-center gap-2 ${textMuted} hover:text-white transition-colors cursor-pointer`}>
                  <i className="ri-mail-line text-sm text-[#ffffff] flex-shrink-0" />
                  <span className="text-sm">ventas@spartechrd.com</span>
                </a>
                <a href="https://wa.me/18492081166" target="_blank" rel="nofollow noreferrer"
                  className="flex items-center gap-2 text-white hover:text-green-300 transition-colors cursor-pointer">
                  <i className="ri-whatsapp-line text-sm flex-shrink-0 text-[#ffffff]" />
                  <span className="text-sm font-semibold">WhatsApp Directo</span>
                </a>
              </div>
            </div>

            {/* Address + Hours */}
            <div className={`${cardBg} rounded-2xl p-5 sm:p-6 border ${border}`}>
              <h4 className="text-white font-bold uppercase tracking-widest text-xs mb-4 sm:mb-5">{t('footer_location')}</h4>
              <a
                href="https://maps.app.goo.gl/fiVfv1yGkZk2UDSG6"
                target="_blank"
                rel="nofollow noreferrer"
                className={`flex items-start gap-2 ${textMuted} hover:text-white transition-colors cursor-pointer mb-3 sm:mb-4`}
              >
                <i className="ri-map-pin-line text-sm mt-0.5 flex-shrink-0 text-[#ffffff]" />
                <span className="text-xs leading-relaxed">C/Octavio Mejía Ricart Esq. C/ Aruba #18A, Ens. Ozama, Santo Domingo Este</span>
              </a>
              <div className="space-y-1.5">
                {[
                  { daysKey: 'footer_mon_fri', hours: '9:00 AM – 6:00 PM', active: true },
                  { daysKey: 'footer_sun', hoursKey: 'footer_closed', active: false },
                ].map((h) => (
                  <div key={h.daysKey} className="flex justify-between items-center text-xs">
                    <span className={textMuted}>{t(h.daysKey)}</span>
                    <span className={h.active ? 'text-zinc-200' : 'text-red-400'}>{h.hoursKey ? t(h.hoursKey) : h.hours}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Bottom bar */}
          <div className={`border-t ${border} pt-5 sm:pt-6 flex flex-col sm:flex-row items-center justify-between gap-3`}>
            <p className={`${textMuted} text-xs text-center sm:text-left`}>
              &copy; {new Date().getFullYear()} <strong className="text-red-500">SpartechRD RD</strong>. {t('footer_rights')}
            </p>
            <div className="flex flex-wrap items-center justify-center gap-3 sm:gap-4">
              {['Política de Privacidad', 'Términos de Uso'].map((txt) => (
                <span key={txt} className={`${textMuted} text-xs hover:text-white transition-colors cursor-pointer`}>{txt}</span>
              ))}
              <div className="flex items-center gap-2">
                <span className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
                <span className="text-green-400 text-[10px] font-bold">{t('footer_system')}</span>
              </div>
              <span className={`${textMuted} text-[10px] flex items-center gap-1`}>
                <i className="ri-flashlight-line text-[#ffffff]" />
                Powered by <strong className="text-zinc-300">Innoflex Technology</strong>
              </span>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}